import React, { useEffect, useState } from 'react';
import '@/assets/app/css/quiz.css';
import FrameBox from '@/shared/components/ui/new/FrameBox';
import { vAppAjax } from '@/shared/utils/CommonUtil';
import logger from '@/shared/utils/Logger';
import AnswerOImage from '@/assets/app/images/adventure/answer_O.png';
import AnswerXImage from '@/assets/app/images/adventure/answer_X.png';
import loadingQuizImage from '@/assets/app/images/adventure/loading_quiz.png';
import OxBearImage from '@/assets/app/images/common/bottom/ox-bear.png';
import CorrectBearImage from '@/assets/app/images/common/bottom/correct-bear.png';
import IncorrectBearImage from '@/assets/app/images/common/bottom/incorrect-bear.png';
import AppButton from '@/shared/components/ui/new/AppButton';
import { useModal } from '@/shared/components/ModalProvider';
import QuizResultDetail from './QuizResult';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import { SegmentedProgressBar } from '../common/SegmentedProgressBar';

interface QuizProps {
  chapter?: ChapterData;
  question?: string;
  options?: string[];
  correctAnswer?: number; // 정답의 인덱스 (0-3)
  onNext?: () => void;
  onClose?: () => void;
}

type QuizState = 'initial' | 'selected' | 'answered';

const Quiz: React.FC<QuizProps> = ({ chapter, onNext, onClose }) => {
  const [session, setSession] = useState<QuizSession | null>(null);
  const [currentOrder, setCurrentOrder] = useState(1);
  const [quiz, setQuiz] = useState<QuizDetail | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | boolean | null>(null);
  const [answerResult, setAnswerResult] = useState<QuizAnswerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedData, setCompletedData] = useState<QuizSessionResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const modal = useModal();
  const { openSheet } = useBottomSheetStore();

  // 세션 시작 및 퀴즈 로드
  useEffect(() => {
    if (!chapter) {
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
    initializeQuiz(chapter);
  }, []);

  // 현재 순서 변경 시 퀴즈 로드
  useEffect(() => {
    if (session) {
      loadQuiz(currentOrder);
    }
  }, [currentOrder, session]);

  const initializeQuiz = async (chapter: ChapterData, isRetry = false) => {
    try {
      const chapterId = chapter?.chapterId;
      const eraId = chapter?.eraId;
      const eraCategoryId = chapter?.eraCategoryId;
      const personId = chapter?.personId;

      // 세션 시작
      const newSession = await startQuizSession({
        chapterId,
        eraId,
        eraCategoryId,
        personId,
        isRetry,
      });

      // 첫 번째 퀴즈 로드
      await loadQuiz(1);
    } catch (error) {
      console.error('퀴즈 초기화 실패:', error);
      modal.error('퀴즈를 시작하는데 실패했습니다.');
    }
  };

  const loadQuiz = async (order: number) => {
    try {
      setCompletedData(null);
      setSelectedAnswer(null);
      setAnswerResult(null);
      setSubmitting(false);
      setCurrentOrder(order);

      if (!chapter) {
        return;
      }

      await getQuizByOrder(chapter.chapterId, order);
    } catch (error) {
      console.error('퀴즈 로딩 실패:', error);
      modal.error('퀴즈를 불러오는데 실패했습니다.');
    }
  };

  const handleMultipleChoiceSelect = (choiceNumber: number) => {
    if (answerResult) return; // 이미 제출한 경우
    setSelectedAnswer(choiceNumber);
  };

  const handleOXSelect = (answer: boolean) => {
    if (answerResult) return; // 이미 제출한 경우
    setSelectedAnswer(answer);
  };

  const handleSubmit = async () => {
    if (selectedAnswer === null || !session || !quiz) {
      modal.warn('답을 선택해주세요.');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = {
        sessionId: session.sessionId,
        questionId: quiz.questionId,
        memberChoice: typeof selectedAnswer === 'number' ? selectedAnswer : undefined,
        memberOxAnswer: typeof selectedAnswer === 'boolean' ? selectedAnswer : undefined,
      };

      const result = await submitQuizAnswer(submitData);
    } catch (error: any) {
      console.error('답안 제출 실패:', error);
      modal.error(error.response?.data?.message || '답안 제출 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!quiz) return;

    if (currentOrder < quiz.totalQuestions) {
      setCurrentOrder(currentOrder + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (!session) return;

    try {
      await completeQuizSession(session.sessionId);
    } catch (error: any) {
      console.error('퀴즈 완료 처리 실패:', error);
      modal.error(error.response?.data?.message || '퀴즈 완료 처리 중 오류가 발생했습니다.');
    }
  };

  const onBack = () => {
    openSheet('exit-quiz', { sessionId: session?.sessionId, onCancel: onClose });
  };

  /**
   * 챕터별 전체 퀴즈 목록 조회
   * @param params
   */
  const getQuizList = async () => {
    await vAppAjax(
      'get',
      'quiz/list/${chapterId}',
      {},
      (response: AxiosResponse<CommonResult<QuizSession>>) => {
        const resultData = response.data;
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 특정 순서의 퀴즈 조회
   * @param params
   */
  const getQuizByOrder = async (chapterId: number, order: number) => {
    await vAppAjax(
      'post',
      `quiz/${chapterId}/${order}`,
      {},
      (response: AxiosResponse<CommonResult<QuizDetail>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setQuiz(resultData.result);
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 퀴즈 세션 시작
   * @param params
   */
  const startQuizSession = async (params: QuizSessionStartRequest) => {
    await vAppAjax(
      'post',
      'quiz/session/start',
      params,
      (response: AxiosResponse<CommonResult<QuizSession>>) => {
        const resultData = response.data;

        if (resultData.code === 200) {
          resultData.result.isRetry = params.isRetry;
          setSession(resultData.result);
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 답안 제출
   * @param params
   */
  const submitQuizAnswer = async (params: QuizAnswerSubmit) => {
    await vAppAjax(
      'post',
      'quiz/session/answer',
      params,
      (response: AxiosResponse<CommonResult<QuizAnswerResponse>>) => {
        const resultData = response.data;

        if (resultData.code === 200) {
          setAnswerResult(resultData.result);
        } else {
          console.warn(resultData.message);
          modal.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 퀴즈 세션 완료
   * @param params
   */
  const completeQuizSession = async (sessionId: number) => {
    await vAppAjax(
      'post',
      `quiz/session/complete/${sessionId}`,
      {},
      (response: AxiosResponse<CommonResult<QuizSessionResult>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setIsCompleted(true);
          setCompletedData(resultData.result);
        } else {
          console.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  /**
   * 세션 진행 상황 조회
   * @param params
   */
  const getSessionProgress = async (sessionId: number) => {
    await vAppAjax(
      'get',
      `quiz/session/progress/${sessionId}`,
      {},
      (response: AxiosResponse<CommonResult<QuizSessionResult>>) => {
        const resultData = response.data;
        // if (resultData.code === 200) {
        //   setEraCategoryList(resultData.result);
        // } else {
        //   console.warn(resultData.message);
        // }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  if (loading || !quiz || !session) {
    return <img src={loadingQuizImage} className="w-full h-full" />;
  }

  if (isCompleted && completedData) {
    return (
      <QuizResultDetail
        completedData={completedData}
        chapter={chapter}
        session={session}
        retryQuiz={() => {
          setIsCompleted(false);
          initializeQuiz(chapter!, true);
        }}
      />
    );
  }

  return (
    <>
      <div className="quiz-container">
        <div className="quiz-header">
          {chapter && chapter.questionCount > currentOrder ? (
            <button className="close-button" onClick={onBack}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#999"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : (
            <></>
          )}
          <h2 className="quiz-title">{chapter?.personName}</h2>
          <span style={{ color: '#14A1C8' }}>{chapter?.chapterTitle}</span>
        </div>
        <SegmentedProgressBar
          color={chapter?.themeColor}
          current={currentOrder}
          total={chapter?.questionCount}
        />
        <FrameBox
          style={{
            margin: '15px 0 30px 0',
            fontSize: 20,
            minHeight: 200,
            padding: '15px 30px',
          }}
        >
          {quiz.questionText}
          {answerResult && (
            <div className={`answer-feedback ${answerResult.isCorrect ? 'correct' : 'wrong'}`}>
              <div className="feedback-content">
                <span className="feedback-text">
                  {answerResult.isCorrect
                    ? '정답입니다.'
                    : `정답: ${answerResult.questionType === 'MULTIPLE_CHOICE' ? quiz[`choice${answerResult.correctAnswer}`] : answerResult.oxAnswer}`}
                </span>
              </div>
            </div>
          )}
        </FrameBox>
        {/* 4지선다형 */}
        {quiz.questionType === 'MULTIPLE_CHOICE' && (
          <div className="quiz-choices multiple-choice">
            {[
              { num: 1, text: quiz.choice1 },
              { num: 2, text: quiz.choice2 },
              { num: 3, text: quiz.choice3 },
              { num: 4, text: quiz.choice4 },
            ].map(choice => (
              <button
                key={choice.num}
                className={`choice-button ${selectedAnswer === choice.num ? 'selected' : ''} ${
                  answerResult && answerResult.correctAnswer === choice.num ? 'correct' : ''
                } ${
                  answerResult && selectedAnswer === choice.num && !answerResult.isCorrect
                    ? 'wrong'
                    : ''
                }`}
                onClick={() => handleMultipleChoiceSelect(choice.num)}
                disabled={answerResult !== null}
              >
                <div className="choice-content">
                  <span className="choice-label">{choice.text}</span>
                </div>
              </button>
            ))}
          </div>
        )}
        {/* O/X형 */}
        {quiz.questionType === 'OX' && (
          <div className="quiz-choices ox-choice">
            <button
              className={`ox-button o-button ${selectedAnswer === true ? 'selected' : ''} ${
                answerResult && answerResult.correctOxAnswer === true ? 'correct' : ''
              } ${answerResult && selectedAnswer === true && !answerResult.isCorrect ? 'wrong' : ''}`}
              onClick={() => handleOXSelect(true)}
              disabled={answerResult !== null}
            >
              <div className="ox-icon">
                <img src={AnswerOImage} />
              </div>
            </button>
            <button
              className={`ox-button x-button ${selectedAnswer === false ? 'selected' : ''} ${
                answerResult && answerResult.correctOxAnswer === false ? 'correct' : ''
              } ${
                answerResult && selectedAnswer === false && !answerResult.isCorrect ? 'wrong' : ''
              }`}
              onClick={() => handleOXSelect(false)}
              disabled={answerResult !== null}
            >
              <div className="ox-icon">
                <img src={AnswerXImage} />
              </div>
            </button>
          </div>
        )}
        {/* 해설 */}
        {/* {answerResult && answerResult.explanation && (
        <div className="quiz-explanation">
          <h3>💡 해설</h3>
          <p>{answerResult.explanation}</p>
        </div>
      )} */}
        <div className="bottom-btn-group">
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              height: 120,
              marginBottom: 5,
            }}
          >
            <img
              src={
                answerResult
                  ? answerResult.isCorrect
                    ? CorrectBearImage
                    : IncorrectBearImage
                  : OxBearImage
              }
              style={{ maxWidth: 'max-content' }}
            />
          </div>
          <AppButton
            onConfirm={!answerResult ? handleSubmit : handleNext}
            backgroundColor={
              answerResult ? (answerResult.isCorrect ? '#14A1C8' : '#EC5050') : '#14A1C8'
            }
            boxShadowColor={
              answerResult ? (answerResult.isCorrect ? undefined : '#EC5050') : undefined
            }
            message={!answerResult ? '정답 확인' : '확인'}
          />
        </div>
      </div>
    </>
  );
};

export default Quiz;
